import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Edit05, Trash01 } from "@untitledui/icons";
import { useAuth } from "@/context/AuthProvider";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/base/buttons/button";
import { Dialog, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import CustomerForm from "./CustomerForm";
import {
  getCustomer,
  updateCustomer,
  deleteCustomer,
  type CustomerWithVehicles,
  type CreateCustomerPayload,
} from "@/api/customers";
import { getInvoices, type InvoiceWithRelations } from "@/api/invoices";

const CAN_EDIT = ["admin", "owner", "manager"];
const CAN_DELETE = ["admin", "owner"];

export default function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [customer, setCustomer] = useState<CustomerWithVehicles | null>(null);
  const [invoices, setInvoices] = useState<InvoiceWithRelations[]>([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const canEdit = CAN_EDIT.includes(user?.role ?? "");
  const canDelete = CAN_DELETE.includes(user?.role ?? "");

  const fetchCustomer = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getCustomer(id);
      setCustomer(data);
    } catch {
      navigate("/customers", { replace: true });
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchInvoices = useCallback(async () => {
    if (!id) return;
    try {
      const data = await getInvoices({ customerId: id, limit: 100 });
      setInvoices(data.data);
    } catch {
      // handled
    }
  }, [id]);

  useEffect(() => {
    fetchCustomer();
    fetchInvoices();
  }, [fetchCustomer, fetchInvoices]);

  const handleEdit = async (payload: CreateCustomerPayload) => {
    if (!id) return;
    setSaving(true);
    try {
      await updateCustomer(id, payload);
      setEditOpen(false);
      fetchCustomer();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    setSaving(true);
    try {
      await deleteCustomer(id);
      navigate("/customers", { replace: true });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !customer) {
    return (
      <PageLayout title="Loading...">
        <div className="py-12 text-center text-text-tertiary">Loading customer...</div>
      </PageLayout>
    );
  }

  const address = customer.address;
  const addressStr = address
    ? [address.street, address.city, address.state, address.zip].filter(Boolean).join(", ")
    : null;

  return (
    <PageLayout
      title={`${customer.firstName} ${customer.lastName}`}
      subtitle={customer.email ?? undefined}
      actions={
        <div className="flex items-center gap-2">
          {canEdit && (
            <Button
              size="md"
              color="secondary"
              iconLeading={Edit05}
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
          )}
          {canDelete && (
            <Button
              size="md"
              color="primary-destructive"
              iconLeading={Trash01}
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          )}
        </div>
      }
    >
      <div className="mb-6">
        <Button
          size="sm"
          color="link-gray"
          iconLeading={ArrowLeft}
          onClick={() => navigate("/customers")}
        >
          Back to Customers
        </Button>
      </div>

      {/* Customer Info */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary p-4 sm:p-6 mb-6 sm:mb-8">
        <h2 className="text-md font-semibold text-text-primary mb-4">Customer Information</h2>
        <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          <div>
            <dt className="text-text-tertiary">Name</dt>
            <dd className="text-text-primary font-medium mt-0.5">
              {customer.firstName} {customer.lastName}
            </dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Email</dt>
            <dd className="text-text-primary mt-0.5">{customer.email ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Phone</dt>
            <dd className="text-text-primary mt-0.5">{customer.phone ?? "—"}</dd>
          </div>
          <div>
            <dt className="text-text-tertiary">Address</dt>
            <dd className="text-text-primary mt-0.5">{addressStr ?? "—"}</dd>
          </div>
          {customer.notes && (
            <div className="md:col-span-2">
              <dt className="text-text-tertiary">Notes</dt>
              <dd className="text-text-primary mt-0.5">{customer.notes}</dd>
            </div>
          )}
        </dl>
      </div>

      {/* Vehicles */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary">
        <div className="px-6 py-4 border-b border-border-secondary">
          <h2 className="text-md font-semibold text-text-primary">
            Vehicles ({customer.vehicles?.length ?? 0})
          </h2>
        </div>
        {customer.vehicles?.length > 0 ? (
          <>
            {/* Mobile cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 xl:hidden">
              {customer.vehicles.map((v) => (
                <div
                  key={v.id}
                  className="rounded-lg border border-border-secondary p-3"
                >
                  <p className="font-medium text-text-primary">
                    {v.make} {v.model}
                  </p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-sm text-text-tertiary">
                    <span>{v.year}</span>
                    <span>{v.licensePlate}</span>
                    {v.color && <span>{v.color}</span>}
                    {v.mileage != null && <span>{v.mileage.toLocaleString()} km</span>}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <table className="hidden xl:table w-full text-sm">
              <thead>
                <tr className="border-b border-border-secondary bg-bg-secondary">
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Vehicle</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Year</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Reg No</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Color</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Mileage</th>
                </tr>
              </thead>
              <tbody>
                {customer.vehicles.map((v) => (
                  <tr
                    key={v.id}
                    className="border-b border-border-secondary last:border-0"
                  >
                    <td className="px-6 py-4 font-medium text-text-primary">
                      {v.make} {v.model}
                    </td>
                    <td className="px-6 py-4 text-text-secondary">{v.year}</td>
                    <td className="px-6 py-4 text-text-secondary">{v.licensePlate}</td>
                    <td className="px-6 py-4 text-text-secondary">{v.color ?? "—"}</td>
                    <td className="px-6 py-4 text-text-secondary">
                      {v.mileage != null ? v.mileage.toLocaleString() : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="px-6 py-8 text-center text-text-tertiary">
            No vehicles registered for this customer.
          </div>
        )}
      </div>

      {/* Invoices */}
      <div className="rounded-xl border border-border-secondary bg-bg-primary mt-6 sm:mt-8">
        <div className="px-4 sm:px-6 py-4 border-b border-border-secondary">
          <h2 className="text-md font-semibold text-text-primary">
            Invoices ({invoices.length})
          </h2>
        </div>
        {invoices.length > 0 ? (
          <>
            {/* Mobile cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 xl:hidden">
              {invoices.map((inv) => (
                <div
                  key={inv.id}
                  className="rounded-lg border border-border-secondary p-3 cursor-pointer"
                  onClick={() => navigate(`/invoices/${inv.id}`)}
                >
                  <div className="flex items-center justify-between">
                    <p className="font-medium text-text-primary">#{inv.invoiceNumber}</p>
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {inv.status}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between text-sm text-text-tertiary">
                    <span>Due: {inv.dueDate}</span>
                    <span className="font-medium text-text-primary">
                      {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(inv.total)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table */}
            <table className="hidden xl:table w-full text-sm">
              <thead>
                <tr className="border-b border-border-secondary bg-bg-secondary">
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Invoice #</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Status</th>
                  <th className="px-6 py-3 text-left font-medium text-text-tertiary">Due Date</th>
                  <th className="px-6 py-3 text-right font-medium text-text-tertiary">Total</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-b border-border-secondary last:border-0 hover:bg-bg-secondary_hover cursor-pointer transition-colors"
                    onClick={() => navigate(`/invoices/${inv.id}`)}
                  >
                    <td className="px-6 py-4 font-medium text-text-primary">#{inv.invoiceNumber}</td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${inv.status === "paid" ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                        {inv.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-text-secondary">{inv.dueDate}</td>
                    <td className="px-6 py-4 text-right font-medium text-text-primary">
                      {new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(inv.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : (
          <div className="px-6 py-8 text-center text-text-tertiary">
            No invoices for this customer.
          </div>
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogTitle>Edit Customer</DialogTitle>
        <DialogDescription>Update the customer's information below.</DialogDescription>
        <CustomerForm
          key={customer.id}
          defaultValues={customer}
          onSubmit={handleEdit}
          onCancel={() => setEditOpen(false)}
          isLoading={saving}
        />
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogTitle>Delete Customer</DialogTitle>
        <DialogDescription>
          Are you sure you want to delete {customer.firstName} {customer.lastName}? This action
          cannot be undone.
        </DialogDescription>
        <div className="flex justify-end gap-3">
          <Button size="md" color="secondary" onClick={() => setDeleteOpen(false)}>
            Cancel
          </Button>
          <Button
            size="md"
            color="primary-destructive"
            isLoading={saving}
            isDisabled={saving}
            onClick={handleDelete}
          >
            Delete
          </Button>
        </div>
      </Dialog>
    </PageLayout>
  );
}
